import {useCallback, useMemo, useState} from 'react'
import {useSidebarSearchAnnouncement} from '@github-ui/layout-primitives/use-sidebar-search-announcement'
import {useDebounce} from '@github-ui/use-debounce'
import type {SidebarSelectedWorkflow} from '../routes/workflows-layout-route'
import type {Workflow} from '../views/models'
import {useWorkflowPagination, type WorkflowSidebarSortMode} from './use-workflow-pagination'
import {useActionsAnalytics} from './use-actions-analytics'

const SEARCH_DEBOUNCE_MS = 250

interface UseWorkflowSidebarStateArgs {
  initialWorkflows: Workflow[]
  totalCount: number
  owner: string
  repo: string
  selectedWorkflow?: SidebarSelectedWorkflow | null
}

export interface WorkflowSidebarState {
  workflows: Workflow[]
  searchFilter: string
  onFilterChange: (value: string, announcementElement?: HTMLElement) => void
  sortMode: WorkflowSidebarSortMode
  onSortModeChange: (mode: WorkflowSidebarSortMode) => void
  hasMore: boolean
  loading: boolean
  loadError: boolean
  /** True while a new search term or sort order is being fetched. */
  searching: boolean
  searchError: boolean
  retrySearch: () => void
  loadMore: (
    announcementElement: HTMLElement,
  ) => Promise<{loadedFinalPage: boolean; hadError: boolean; skipped: boolean}>
}

export function useWorkflowSidebarState({
  initialWorkflows,
  totalCount,
  owner,
  repo,
  selectedWorkflow,
}: UseWorkflowSidebarStateArgs): WorkflowSidebarState {
  const track = useActionsAnalytics()
  const trackSearch = useDebounce(() => track('workflow_sidebar.search', 'ref_loc:actions_workflow_sidebar'), 400, {
    onChangeBehavior: 'cancel',
  })
  // `searchFilter` drives the input so typing stays responsive; `query` is what the server sees.
  const [searchFilter, setSearchFilter] = useState('')
  const [query, setQuery] = useState('')
  const [sortMode, setSortMode] = useState<WorkflowSidebarSortMode>('default')
  const debouncedSetQuery = useDebounce(setQuery, SEARCH_DEBOUNCE_MS)

  const pagination = useWorkflowPagination({
    initialWorkflows,
    totalCount,
    owner,
    repo,
    query,
    sortMode,
    selectedWorkflow,
  })

  const setSearchAnnouncementElement = useSidebarSearchAnnouncement(query, pagination.totalCount, 'workflow', {
    pending: pagination.searching,
    failed: pagination.searchError,
  })

  const updateSearchFilter = useCallback(
    (value: string, announcementElement?: HTMLElement) => {
      if (announcementElement) setSearchAnnouncementElement(announcementElement)
      setSearchFilter(value)

      trackSearch.cancel()
      if (value.trim()) trackSearch()
      if (value === '') {
        // Clearing is deliberate rather than mid-typing, so it should not wait out the debounce.
        debouncedSetQuery.cancel()
        setQuery('')
      } else {
        debouncedSetQuery(value)
      }
    },
    [debouncedSetQuery, setSearchAnnouncementElement, trackSearch],
  )

  const {workflows, hasMore, loading, loadError, searching, searchError, retrySearch, loadMore} = pagination

  return useMemo(
    () => ({
      workflows,
      searchFilter,
      onFilterChange: updateSearchFilter,
      sortMode,
      onSortModeChange: setSortMode,
      hasMore,
      loading,
      loadError,
      searching,
      searchError,
      retrySearch,
      loadMore,
    }),
    [
      hasMore,
      loadError,
      loadMore,
      loading,
      retrySearch,
      searchError,
      searchFilter,
      searching,
      sortMode,
      updateSearchFilter,
      workflows,
    ],
  )
}
